import { jsx as _jsx } from "react/jsx-runtime";
import { useLazyQuery } from '@apollo/client';
import { TextLink } from '@guildeducationinc/recess';
import classNames from 'classnames';
import parse, { domToReact } from 'html-react-parser';
import { useAlert } from '..';
import { useLocale } from '../..';
import { GET_URL } from '../../graphql/queries';
import { useLazyFetchResources } from '../../hooks/useLazyFetchResources';
import styles from './RichTextViewer.module.scss';
const RESOURCE_LINK_PREFIX = 'resource:';
export const RichTextViewer = ({ html, className, customLinkProps, customResourceProps, }) => {
    const [getUrl] = useLazyQuery(GET_URL);
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const { fetchResources } = useLazyFetchResources({
        clientId: customResourceProps?.clientId || 0,
        includePublicUrl: true,
    });
    if (!html)
        return null;
    const handleResourceLinkClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Open window immediately to avoid popup blocker
        const newWindow = window.open('', '_blank');
        const resourceId = e.currentTarget.getAttribute('href')?.split(':')[1];
        const resources = await fetchResources(Number(resourceId));
        if (resources.length > 0 && newWindow) {
            newWindow.location.href = resources[0].publicUrl;
        }
        else {
            // Close the window if there was an error
            if (newWindow) {
                newWindow.close();
            }
            showAlert(t('Unable to fetch resource'));
        }
    };
    const handleCustomLinkClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Open window immediately to avoid popup blocker
        const newWindow = window.open('', '_blank');
        let href = e.currentTarget.getAttribute('href') ?? '';
        if (href.startsWith('{') && href.endsWith('}')) {
            href = href.slice(1, -1);
        }
        // find the doc in the docs array
        const doc = customLinkProps?.docs.find(doc => doc.s3Key === href || doc.name.endsWith(href));
        if (doc) {
            try {
                const { data } = await getUrl({
                    variables: {
                        input: {
                            s3Key: doc.s3Key,
                            ...(customLinkProps?.extraProps || {}),
                        },
                    },
                });
                if (newWindow) {
                    newWindow.location.href = data.getUrl;
                }
            }
            catch (error) {
                // Close the window if there was an error
                if (newWindow) {
                    newWindow.close();
                }
                console.error(error);
                showAlert('Unable to open link');
            }
        }
    };
    const parsedHtml = parse(html, {
        replace: (domNode) => {
            if (customLinkProps?.docs &&
                domNode.name === 'a' &&
                domNode.attribs?.href?.startsWith('{') &&
                domNode.attribs?.href?.endsWith('}')) {
                return (_jsx(TextLink, { href: domNode.attribs.href, target: customLinkProps?.target || '_blank', onClick: handleCustomLinkClick, children: domToReact(domNode.children) }));
            }
            if (domNode.name === 'a' && domNode.attribs?.href?.startsWith(RESOURCE_LINK_PREFIX)) {
                return (_jsx(TextLink, { href: domNode.attribs.href, target: '_blank', onClick: handleResourceLinkClick, children: domToReact(domNode.children) }));
            }
        },
    });
    return _jsx("div", { className: classNames(styles.richTextViewer, className), children: parsedHtml });
};
